import { useMemo } from "react";
import { Grid3X3, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useRunTargetareMatrice } from "@/hooks/api/mutate/useRunTargetareMatrice";
import { useGetTargetareMatrice } from "@/hooks/api/query/useGetTargetareMatrice";
import { useGetTargetareStats } from "@/hooks/api/query/useGetTargetareStats";
import { useToast } from "@/hooks/use-toast";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";
import { jobActiv } from "@/utils/targetareJoburi";
import {
  definitieRaportMatrice,
  randuriMatrice,
} from "@/utils/targetareRaportMatrice";
import { RaportInteractiv } from "../components/raport/RaportInteractiv";

export default function MatriceTargetare() {
  const { toast } = useToast();
  const stats = useGetTargetareStats();
  const jobInCurs = jobActiv(stats.data?.joburi ?? []);
  const ultimulJobMatrice = stats.data?.joburi.find(
    (j) => j.tip === "matrice" && j.stare === "finalizat"
  );
  const matrice = useGetTargetareMatrice(ultimulJobMatrice?.sfarsit_la ?? null);
  const ruleaza = useRunTargetareMatrice();
  const randuri = useMemo(
    () => randuriMatrice(matrice.data?.randuri ?? []),
    [matrice.data]
  );
  const sumar = matrice.data?.sumar;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-xl font-heading font-semibold">
            Matricea CAEN → sector
          </h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Pentru fiecare cod CAEN din maparea serviciilor NIS2 (aceeași tabelă
            ca formularul de notificare): câte firme active îl au ca CAEN
            principal în bilanț, câte îl au ca activitate autorizată la ONRC,
            câte dintre ele sunt mari sau mijlocii, câte firme are serviciul pe
            listele oficiale și ce spune Termene. „Sugestiile” sunt codurile
            CAEN principale ale firmelor găsite pe liste care nu sunt mapate pe
            serviciul respectiv, deci candidate pentru completarea matricei.
          </p>
        </div>
        <Button
          onClick={() =>
            ruleaza.mutate(undefined, {
              onSuccess: (r) =>
                toast({
                  title: "Recalculare programată",
                  description: `Job #${r.data.job_id} rulează în fundal (câteva minute).`,
                }),
              onError: (e) =>
                toast({
                  title: "Recalcularea a eșuat",
                  description: e.message,
                  variant: "destructive",
                }),
            })
          }
          disabled={ruleaza.isPending || !!jobInCurs}
        >
          {ruleaza.isPending || jobInCurs ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Grid3X3 className="mr-2 h-4 w-4" />
          )}
          Recalculează matricea
        </Button>
      </div>

      {jobInCurs && (
        <p className="rounded-md border border-ds-blue-200 bg-ds-blue-50 p-3 text-sm text-ds-blue-700">
          Job #{jobInCurs.id} ({jobInCurs.tip}) rulează în fundal.
        </p>
      )}

      {matrice.isLoading ? (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : (
        sumar && (
          <div className="space-y-2">
            <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
              {[
                ["Coduri în matrice", sumar.coduri],
                ["Servicii", sumar.servicii],
                ["Coduri fără firme", sumar.fara_firme],
                ["Sugestii de completare", sumar.sugestii],
              ].map(([eticheta, valoare]) => (
                <Card key={String(eticheta)}>
                  <CardContent className="p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      {eticheta}
                    </p>
                    <p className="text-2xl font-heading font-semibold tabular-nums">
                      {formatNumar(Number(valoare))}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Calculată la {formatDataOra(matrice.data?.calculat_la ?? null)}.
            </p>
          </div>
        )
      )}

      {matrice.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Matricea nu a putut fi încărcată: {matrice.error.message}
        </p>
      )}

      <RaportInteractiv
        definitie={definitieRaportMatrice}
        randuri={randuri}
        isLoading={matrice.isLoading}
      />
    </div>
  );
}
